export interface GroupData {
    group_id: string;
    group_name: string;
}

export interface Data {
    id: string;
    name: string;
}

export interface Customer {
    pk: string,
    company_name: string,
    type_code: string,
    contact_name: string,
    billing_address_phone: string,
    contact_email: string,
    billing_address_name: string,
    billing_address_street: string,
    billing_address_street_no: string,
    billing_address_zip_code: string,
    billing_address_city: string,
    billing_address_country: string,
    billing_address_email: string,
    group_id: string,
    admin_name: string,
    admin_email: string,
    created_at: string
}

export interface CustomerCredential {
    rotate: boolean,
    customer_id: string,
    supplier_system_auth_id: string,
    supplier_partner_segment: string,
    secrets: {
        username: string,
        password: string,
        otp_secret_key?: string
    }
}

export interface ImporterAccount {
    id: string,
    supplier_system_auth_id: string,
    supplier_system_partner_id: string,
    supplier_system_partner_name?: string;
    supplier_partner_id?: string,
    supplier_partner_segment: string,
    display_name?: string,
    website?: string,
    note?: string,
    start_time?: string,
    max_duration_hours?: number,
    logo_url?: string,
    secrets?: {
        username: string,
        password: string,
        otp_secret_key?: string
    },
    credentials_id?: string,
    settings?: Record<string, unknown>,
    enabled: boolean,
    rotate: boolean,
}

export interface Credential {
    supplier_system_auth_id: string,
    customer_id: string,
    rotate: boolean,
    verification_status: "unverified" | "verified" | "failed",
    id: string
}

export interface Feature
// id: string,
// name: string,
// description: string,
// price: number,
// renewal_period: number,
// created_at: string
{
    active?: boolean,
    name: string,
    description: string,
    price: number,
    pk: string,
    sk: string,
    created_at: string,
    published: boolean,
    renewal_period: number,
    deleted: boolean
}

export const MAPPING_TYPES = ['EXACT_KEY_MAPPING', 'REGEX_MAPPING'] as const
export type MappingType = typeof MAPPING_TYPES[number]

export const MAPPING_ATTRIBUTES = [
    'vehicle_model',
    'vehicle_model_line',
    'vehicle_color',
    'vehicle_model_normalized',
    'cardealer_vehicle_order_type',
    'vehicle_gear_type',
    'vehicle_fuel_type',
    'leasing_partner_group_id_in_manufacturer_order_system'
] as const
export type MappingAttribute = typeof MAPPING_ATTRIBUTES[number]

export const VEHICLE_BRAND_MAP = {
    'abarth': 'Abarth', 'alfa-romeo': 'Alfa Romeo', 'alpina': 'Alpina', 'ari': 'ARI',
    'aston-martin': 'Aston Martin', 'audi': 'Audi', 'bentley': 'Bentley', 'bmw': 'BMW',
    'bmw_sgate': 'BMW_SGATE', 'byd': 'BYD', 'chevrolet': 'Chevrolet', 'chrysler': 'Chrysler',
    'citroen': 'Citroën', 'cupra': 'Cupra', 'dacia': 'Dacia', 'daihatsu': 'Daihatsu',
    'ds-automobiles': 'DS Automobiles', 'ducati': 'Ducati', 'fiat': 'Fiat', 'fiat-professional': 'Fiat Professional',
    'fisker': 'Fisker', 'ford': 'Ford', 'genesis': 'Genesis', 'goupil': 'Goupil',
    'gwm': 'GWM', 'hako': 'HAKO', 'honda': 'Honda', 'hyundai': 'Hyundai',
    'ineos': 'Ineos', 'infiniti': 'Infiniti', 'isuzu': 'Isuzu', 'iveco': 'Iveco',
    'jaguar': 'Jaguar', 'jeep': 'Jeep', 'kia': 'Kia', 'land-rover': 'Land Rover',
    'leapmotor': 'Leapmotor', 'lexus': 'Lexus', 'lotus': 'Lotus', 'lucid': 'Lucid',
    'lynk-&-co': 'Lynk & Co', 'man': 'MAN', 'maserati': 'Maserati', 'maxus': 'Maxus',
    'mazda': 'Mazda', 'mercedes': 'Mercedes', 'mercedes-nutzfahrzeuge': 'Mercedes Nutzfahrzeuge',
    'mg': 'MG', 'mini': 'Mini',
    'mitsubishi': 'Mitsubishi', 'mubea': 'Mubea', 'nanjing-jiayuan': 'NANJING JIAYUAN',
    'nio': 'NIO', 'nissan': 'Nissan', 'opel': 'Opel', 'peugeot': 'Peugeot',
    'polestar': 'Polestar', 'porsche': 'Porsche', 'renault': 'Renault', 'rivian': 'Rivian',
    'saab': 'Saab', 'saic': 'SAIC', 'seat': 'Seat', 'skoda': 'Škoda', 'smart': 'Smart',
    'ssangyong': 'SsangYong', 'subaru': 'Subaru', 'suzuki': 'Suzuki', 'tesla': 'Tesla',
    'toyota': 'Toyota', 'triumph': 'Triumph', 'tyne': 'TYNE', 'volvo': 'Volvo', 'vw': 'VW',
    'vw-nutzfahrzeuge': 'VW Nutzfahrzeuge', 'xpeng': 'XPENG',
    'yamaha': 'Yamaha', 'zeekr': 'Zeekr'
} as const

export type VehicleBrand = typeof VEHICLE_BRAND_MAP[keyof typeof VEHICLE_BRAND_MAP]

export const MAPPING_SCOPE_TYPES = ['BRAND', 'SYSTEM'] as const
export type MappingScopeType = typeof MAPPING_SCOPE_TYPES[number]

export interface Mapping {
    attribute: MappingAttribute
    scope_type: MappingScopeType
    brand?: VehicleBrand
    brand_normalized?: string
    supplier_system_partner_id?: string
    mapping_type: MappingType
    raw_value: string
    normalized_value: string
    exclude_value?: string
    created_at?: string
    updated_at?: string
}

export interface MappingSearchResponse {
    items: Mapping[]
    next_token?: string
}

export type MappingInput = Pick<Mapping, 'brand' | 'mapping_type' | 'raw_value' | 'normalized_value' | 'exclude_value'>

export type MappingIdentifier = Pick<Mapping, 'mapping_type' | 'raw_value'>

export interface MappingQueryParams extends Partial<MappingIdentifier> {
    limit?: number
    next_token?: string
}

export interface CustomerFilter {
    pk: string,
    sk: string,
    filter: Array<Array<{[key: string]: Array<string>}>>,
    created_at: string
}

export type FilterCriteria = Array<Array<{[key: string]: Array<string>}>>

export const API_KEY_PERMISSIONS = [
    'read',
    'write',
    'data-entry:write:production-orders',
    'data-entry:write:custom-dealer-orders',
    'data-entry:write:leasing-orders',
    'data-entry:write:fleet-orders',
    'data-entry:write:admission-services',
    'data-entry:write:assembler-services',
    'data-entry:write:detailer-services',
    'data-entry:write:logistics-services',
    'data-entry:write:logistics-eigenachs-services',
    'data-entry:write:recall-services',
] as const

export interface ApiKeySupplierMetadata {
    supplier_system_partner_id: string
    supplier_partner_id: string
}

export interface ApiKey {
    api_key_id: string
    full_api_key_alias: string
    name: string
    description: string
    enabled: boolean
    filter: FilterCriteria
    permissions: string[]
    scope?: string
    metadata?: { [key: string]: string }
    supplier_metadata?: ApiKeySupplierMetadata[]
    created_at: string
}

export type ApiKeyCreateInput = Pick<ApiKey, 'name' | 'description' | 'filter' | 'permissions' | 'scope' | 'metadata' | 'supplier_metadata'>

export interface ApiKeyCreateResponse extends ApiKey {
    raw_key: string
}

export type ApiKeyUpdateInput = Pick<ApiKey, 'name' | 'description' | 'filter' | 'permissions' | 'supplier_metadata'>

export interface IUserInsight {
    email: string
    tenant_id: string
    count_24h: number
    count_7d: number
    count_30d: number
    features: { [key: string]: number }
}
